/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { DOPPLER_LAUNCH_HOOK_V2_PROGRAM_ADDRESS } from '../programs';

export const REMOVE_COSIGNER_DISCRIMINATOR = new Uint8Array([
  174, 11, 171, 252, 179, 99, 220, 27,
]);

export function getRemoveCosignerDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    REMOVE_COSIGNER_DISCRIMINATOR,
  );
}

export type RemoveCosignerInstruction<
  TProgram extends string = typeof DOPPLER_LAUNCH_HOOK_V2_PROGRAM_ADDRESS,
  TAccountAdminAuthority extends string | AccountMeta<string> = string,
  TAccountConfig extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAdminAuthority extends string
        ? ReadonlySignerAccount<TAccountAdminAuthority> &
            AccountSignerMeta<TAccountAdminAuthority>
        : TAccountAdminAuthority,
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      ...TRemainingAccounts,
    ]
  >;

export type RemoveCosignerInstructionData = {
  discriminator: ReadonlyUint8Array;
  cosigner: Address;
};

export type RemoveCosignerInstructionDataArgs = { cosigner: Address };

export function getRemoveCosignerInstructionDataEncoder(): FixedSizeEncoder<RemoveCosignerInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['cosigner', getAddressEncoder()],
    ]),
    (value) => ({ ...value, discriminator: REMOVE_COSIGNER_DISCRIMINATOR }),
  );
}

export function getRemoveCosignerInstructionDataDecoder(): FixedSizeDecoder<RemoveCosignerInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['cosigner', getAddressDecoder()],
  ]);
}

export function getRemoveCosignerInstructionDataCodec(): FixedSizeCodec<
  RemoveCosignerInstructionDataArgs,
  RemoveCosignerInstructionData
> {
  return combineCodec(
    getRemoveCosignerInstructionDataEncoder(),
    getRemoveCosignerInstructionDataDecoder(),
  );
}

export type RemoveCosignerAsyncInput<
  TAccountAdminAuthority extends string = string,
  TAccountConfig extends string = string,
> = {
  adminAuthority: TransactionSigner<TAccountAdminAuthority>;
  config?: Address<TAccountConfig>;
  cosigner: RemoveCosignerInstructionDataArgs['cosigner'];
};

export async function getRemoveCosignerInstructionAsync<
  TAccountAdminAuthority extends string,
  TAccountConfig extends string,
  TProgramAddress extends Address =
    typeof DOPPLER_LAUNCH_HOOK_V2_PROGRAM_ADDRESS,
>(
  input: RemoveCosignerAsyncInput<TAccountAdminAuthority, TAccountConfig>,
  config?: { programAddress?: TProgramAddress },
): Promise<
  RemoveCosignerInstruction<
    TProgramAddress,
    TAccountAdminAuthority,
    TAccountConfig
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_LAUNCH_HOOK_V2_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    adminAuthority: { value: input.adminAuthority ?? null, isWritable: false },
    config: { value: input.config ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            100, 111, 112, 112, 108, 101, 114, 95, 108, 97, 117, 110, 99, 104,
            95, 104, 111, 111, 107, 95, 118, 50,
          ]),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('adminAuthority', accounts.adminAuthority),
      getAccountMeta('config', accounts.config),
    ],
    data: getRemoveCosignerInstructionDataEncoder().encode(
      args as RemoveCosignerInstructionDataArgs,
    ),
    programAddress,
  } as RemoveCosignerInstruction<
    TProgramAddress,
    TAccountAdminAuthority,
    TAccountConfig
  >);
}

export type RemoveCosignerInput<
  TAccountAdminAuthority extends string = string,
  TAccountConfig extends string = string,
> = {
  adminAuthority: TransactionSigner<TAccountAdminAuthority>;
  config: Address<TAccountConfig>;
  cosigner: RemoveCosignerInstructionDataArgs['cosigner'];
};

export function getRemoveCosignerInstruction<
  TAccountAdminAuthority extends string,
  TAccountConfig extends string,
  TProgramAddress extends Address =
    typeof DOPPLER_LAUNCH_HOOK_V2_PROGRAM_ADDRESS,
>(
  input: RemoveCosignerInput<TAccountAdminAuthority, TAccountConfig>,
  config?: { programAddress?: TProgramAddress },
): RemoveCosignerInstruction<
  TProgramAddress,
  TAccountAdminAuthority,
  TAccountConfig
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_LAUNCH_HOOK_V2_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    adminAuthority: { value: input.adminAuthority ?? null, isWritable: false },
    config: { value: input.config ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('adminAuthority', accounts.adminAuthority),
      getAccountMeta('config', accounts.config),
    ],
    data: getRemoveCosignerInstructionDataEncoder().encode(
      args as RemoveCosignerInstructionDataArgs,
    ),
    programAddress,
  } as RemoveCosignerInstruction<
    TProgramAddress,
    TAccountAdminAuthority,
    TAccountConfig
  >);
}

export type ParsedRemoveCosignerInstruction<
  TProgram extends string = typeof DOPPLER_LAUNCH_HOOK_V2_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    adminAuthority: TAccountMetas[0];
    config: TAccountMetas[1];
  };
  data: RemoveCosignerInstructionData;
};

export function parseRemoveCosignerInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedRemoveCosignerInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { adminAuthority: getNextAccount(), config: getNextAccount() },
    data: getRemoveCosignerInstructionDataDecoder().decode(instruction.data),
  };
}
